// The lead grade matrix: engagement down the side, fit across the top, one
// cell per combination.
//
// Three readings share this grid. The editable one holds the grade each cell
// maps to and is the control; the two beneath it ("More details") hold the
// validation set's conversion rate and share of leads per cell, tinted so the
// eye finds the heavy cells without reading sixteen numbers. One component
// rather than three tables, so the rows and columns cannot drift apart between
// the control and the readings that justify it.
//
// Axis marks come from the two segment components the rest of the product
// uses, stars for fit and flames for engagement, so the matrix reads in the
// same marks as the lead cards on the Signals page.
import React from 'react';

import { LeadGradeValue } from '../../../models/lead_grade/LeadGradePageModel';
import { compareCustomerFits, compareLtb } from '../../../utils';
import EngagementFlames from './EngagementFlames';
import FitStars from './FitStars';
import { segmentFill, segmentInk, segmentLabel } from './segmentVocabulary';
import './gradeMatrix.css';

export const GRADES: LeadGradeValue[] = ['A', 'B', 'C', 'D', 'E'];

type Cell = { ltb: string; fit: string };

/** Rows best first, columns best first, as the product orders both. */
export function matrixAxes<T>(matrix: {
  [ltb: string]: { [fit: string]: T };
}): {
  rows: string[];
  columns: string[];
} {
  const rows = Object.keys(matrix)
    .sort((a, b) => compareLtb(a as never, b as never))
    .reverse();
  const columns = Object.keys(matrix[rows[0]] ?? {})
    .sort((a, b) => compareCustomerFits(a as never, b as never))
    .reverse();
  return { rows, columns };
}

/** The next grade down the alphabet, wrapping from E back to A. */
export function nextGrade(grade: LeadGradeValue): LeadGradeValue {
  return GRADES[(GRADES.indexOf(grade) + 1) % GRADES.length];
}

export function GradeMatrix({
  matrix,
  shares,
  onChange,
  disabled,
}: {
  matrix: { [ltb: string]: { [fit: string]: LeadGradeValue } };
  /** Share of leads per cell, as a percentage, printed under the grade. */
  shares?: { [ltb: string]: { [fit: string]: number } };
  onChange?: (cell: Cell, grade: LeadGradeValue) => void;
  disabled?: boolean;
}) {
  const { rows, columns } = matrixAxes(matrix);
  return (
    <div className="gmx">
      <div className="gmx-axis gmx-axis--top">Customer fit</div>
      <div className="gmx-axis gmx-axis--side">Engagement</div>
      <div
        className="gmx-grid"
        style={{ gridTemplateColumns: `auto repeat(${columns.length}, 1fr)` }}
      >
        <div />
        {columns.map((fit) => (
          <div className="gmx-col" key={fit}>
            <FitStars segment={fit} />
            <span>{segmentLabel('customer_fit', fit)}</span>
          </div>
        ))}
        {rows.map((ltb) => (
          <React.Fragment key={ltb}>
            <div className="gmx-row">
              <EngagementFlames segment={ltb} />
              <span>{segmentLabel('behavioral', ltb)}</span>
            </div>
            {columns.map((fit) => {
              const grade = matrix[ltb][fit];
              const share = shares?.[ltb]?.[fit];
              return (
                <button
                  type="button"
                  key={fit}
                  data-testid={`grade-cell-${ltb}-${fit}`}
                  className="gmx-cell"
                  style={{
                    background: segmentFill('lead_grade', grade),
                    color: segmentInk('lead_grade', grade),
                  }}
                  disabled={disabled || !onChange}
                  aria-label={`${segmentLabel(
                    'behavioral',
                    ltb
                  )} engagement, ${segmentLabel(
                    'customer_fit',
                    fit
                  )} fit: grade ${grade}. Click for the next grade.`}
                  title="Step grade"
                  onClick={() => onChange?.({ ltb, fit }, nextGrade(grade))}
                >
                  <span className="gmx-grade">{grade}</span>
                  {share !== undefined && (
                    <span className="gmx-share">
                      {share.toFixed(1)}% of leads
                    </span>
                  )}
                </button>
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

/**
 * A reading per cell, tinted by its size against the largest cell. Rates print
 * one decimal; shares print one decimal of a percent. The tint is a wash of the
 * product's blue, so the heavy cells read without a legend.
 */
export function GradeHeatmap({
  matrix,
  format,
}: {
  matrix: { [ltb: string]: { [fit: string]: number } };
  format: (value: number) => string;
}) {
  const { rows, columns } = matrixAxes(matrix);
  const values = rows.flatMap((ltb) =>
    columns.map((fit) => Number(matrix[ltb][fit]) || 0)
  );
  const max = Math.max(0, ...values);
  return (
    <div className="gmx gmx--reading">
      <div className="gmx-axis gmx-axis--top">Customer fit</div>
      <div className="gmx-axis gmx-axis--side">Engagement</div>
      <div
        className="gmx-grid"
        style={{ gridTemplateColumns: `auto repeat(${columns.length}, 1fr)` }}
      >
        <div />
        {columns.map((fit) => (
          <div className="gmx-col" key={fit}>
            <span>{segmentLabel('customer_fit', fit)}</span>
          </div>
        ))}
        {rows.map((ltb) => (
          <React.Fragment key={ltb}>
            <div className="gmx-row">
              <span>{segmentLabel('behavioral', ltb)}</span>
            </div>
            {columns.map((fit) => {
              const value = Number(matrix[ltb][fit]) || 0;
              const weight = max > 0 ? Math.sqrt(value / max) : 0;
              return (
                <div
                  key={fit}
                  className="gmx-cell gmx-cell--reading"
                  style={{
                    background: `rgba(30, 119, 204, ${(
                      0.06 +
                      0.84 * weight
                    ).toFixed(2)})`,
                    color:
                      weight > 0.45 ? 'var(--app-cta-ink)' : 'var(--app-ink)',
                  }}
                >
                  {format(value)}
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}
